/**
 * MobileSettings — Settings screen with common preferences and, for
 * unauthenticated visitors, a modal gate asking them to register or
 * log in before accessing personal features (Profile, My Lists, etc).
 *
 * Authenticated users see their name/email at the top; the modal is
 * suppressed.
 */
import { Link } from "react-router-dom";
import { User, Info, Share2, Star, Globe, ChevronRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import AuthGateModal from "./AuthGateModal";

const FP_PURPLE = "#653091";
const FP_GREEN = "#9CCA45";

const ROWS = [
  { key: "profile", label: "My Profile", Icon: User, to: "/m/profile", authOnly: true },
  { key: "about",   label: "About Us",   Icon: Info, to: "/m/about" },
  { key: "share",   label: "Share FoodPulse", Icon: Share2, action: "share" },
  { key: "rate",    label: "Rate FoodPulse",  Icon: Star, action: "rate" },
  { key: "web",     label: "Our Website", Icon: Globe, to: "https://foodpulse.net", external: true },
];

export default function MobileSettings() {
  const { user } = useAuth() || {};
  const isGuest = !user;

  const handleAction = (row) => {
    if (row.action === "share") {
      if (navigator.share) {
        navigator.share({ title: "Food Pulse", url: "https://foodpulse.net" }).catch(() => {});
      } else {
        navigator.clipboard?.writeText("https://foodpulse.net");
      }
    } else if (row.action === "rate") {
      window.open("https://apps.apple.com", "_blank"); // placeholder
    }
  };

  return (
    <div className="relative" data-testid="mobile-settings-page">
      <div className="px-5 pt-5">
        <h1 className="text-2xl font-bold mb-4">Settings</h1>
        <ul className="space-y-3">
          {ROWS.map((row) => {
            const disabled = row.authOnly && isGuest;
            const inner = (
              <div
                className="flex items-center justify-between bg-gray-100 rounded-xl px-4 py-4"
                style={{ opacity: disabled ? 0.5 : 1 }}
              >
                <div className="flex items-center gap-3">
                  <row.Icon className="w-5 h-5" />
                  <span className="text-base font-medium">{row.label}</span>
                </div>
                <ChevronRight className="w-4 h-4 opacity-60" />
              </div>
            );
            if (row.action) {
              return (
                <li key={row.key}>
                  <button
                    className="w-full text-left"
                    onClick={() => handleAction(row)}
                    data-testid={`settings-row-${row.key}`}
                  >
                    {inner}
                  </button>
                </li>
              );
            }
            if (row.external) {
              return (
                <li key={row.key}>
                  <a href={row.to} target="_blank" rel="noreferrer" data-testid={`settings-row-${row.key}`}>
                    {inner}
                  </a>
                </li>
              );
            }
            return (
              <li key={row.key}>
                <Link
                  to={disabled ? "#" : row.to}
                  data-testid={`settings-row-${row.key}`}
                  onClick={(e) => disabled && e.preventDefault()}
                >
                  {inner}
                </Link>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Guest gate modal */}
      {isGuest && <AuthGateModal />}
    </div>
  );
}
